/**
 * Scene templates — art-first compositions, one per metaphor (TT-1046).
 *
 * A template says how a story board is composed AROUND its artwork: the
 * anchor's size and placement, where each arc stage sits on the art
 * ([region, fx, fy] inside the region's bbox), which side of the path the
 * ideas hang on, and which object art appears and what it *carries*
 * (the climber's flag holds the decisions; two clouds hold a tension; the
 * sun marks the landing). The solver reads only this file to know the
 * scene, so a new metaphor is a new entry here plus its art. Art is named
 * by symbol_id: the anchor's is a backdrop, an object's a mid.
 *
 * `palette` tints the board to its art (banner fill, accent, tension
 * arrows) and overrides the spec's stock palette; sampled from the backdrops.
 *
 * `terrain` says what a container sits in (board/terrain.js dresses it):
 * `regions` maps the art's manifest regions to a terrain (a foot in the
 * canopy is canopy; on the rapids, water); `horizon` is where the air ends —
 * a fraction of the art's painted height, or a region name whose top edge
 * it is (the iceberg's waterline) — and `below` is the terrain under it.
 * Sky is the ridge zone above the horizon. No `horizon`: nothing to sit in.
 *
 * Coordinates: `at` = [fx, fy] of the sheet; `near` = a stage name (point on
 * the art) with an [dx, dy] offset in sheet units. A stage on the region
 * `sheet` is [fx, fy] of the sheet itself (the open board has no art).
 *
 * `title` / `ideaIcon` override the solver's stock title register and idea
 * icon size (STORY) — the open board sets a big centred title and spot-
 * drawing icons, since nothing else on it is a picture.
 */

export const TEMPLATES = {
  climb: {
    terrain: { horizon: 0.55, below: 'ground' },
    palette: { accent: '#3c6fa8', tint: '#d5e7f5', secondary: '#d9772e' },
    anchor: { symbol_id: 'mountain', scale: 0.7, placement: 'center-low', opacity: 0.95 },
    route: 'trail',
    // The route climbs the left face; ideas hang left of it, the landing idea right.
    stages: {
      opening: ['base', 0.14, 0.62],
      discovery: ['slope', 0.27, 0.72],
      turn: ['slope', 0.4, 0.32],
      landing: ['peak', 0.5, 0.4],
    },
    ideaSide: { opening: 'left', discovery: 'left', turn: 'left', landing: 'right' },
    objects: [
      { id: 'flag', symbol_id: 'person-holding-a-flag', size: 0.19, near: 'landing', offset: [70, -150], role: 'decisions', side: 'right' },
      { id: 'sun', symbol_id: 'sun', size: 0.12, at: [0.9, 0.12], role: 'landing-mark' },
    ],
    tensionsAt: [0.74, 0.22], // sky, right of the peak (clouds)
    themeSlots: ['flank-right', 'corner-left', 'corner-right', 'base-right', 'base-left', 'sky-right', 'left-mid', 'right-mid'],
  },
  growth: {
    terrain: { horizon: 0.55, below: 'ground', regions: { canopy: 'canopy', roots: 'underground' } },
    palette: { accent: '#3aa655', tint: '#d9f26b', secondary: '#2a7ae8' },
    anchor: { symbol_id: 'tree', scale: 0.5, placement: 'center-low', opacity: 0.95 },
    route: 'marks', // the tree is the arc; no trail
    stages: {
      opening: ['roots', 0.35, 0.5],
      discovery: ['trunk', 0.5, 0.7],
      turn: ['trunk', 0.5, 0.2],
      landing: ['canopy', 0.5, 0.45],
    },
    ideaSide: { opening: 'left', discovery: 'right', turn: 'left', landing: 'right' },
    objects: [
      { id: 'seed', symbol_id: 'seed-with-shoots', size: 0.14, near: 'opening', offset: [-260, 40], role: 'opening-mark' },
      { id: 'bird', symbol_id: 'bird', size: 0.12, near: 'landing', offset: [260, -140], role: 'decisions', side: 'right' },
      { id: 'sun', symbol_id: 'sun', size: 0.12, at: [0.9, 0.12], role: 'landing-mark' },
    ],
    tensionsAt: [0.22, 0.22],
    themeSlots: ['left-mid', 'right-mid', 'corner-left', 'corner-right', 'sky-right', 'base-right'],
  },
  iceberg: {
    terrain: { horizon: 'below', below: 'water' },
    palette: { accent: '#1f7f99', tint: '#d3eff4', secondary: '#d9772e' },
    anchor: { symbol_id: 'iceberg', scale: 0.95, placement: 'center-low', opacity: 0.95 },
    route: 'depth', // a sounding line down the berg
    // Depth: the question at the tip, then progressively deeper.
    stages: {
      opening: ['tip', 0.5, 0.35],
      discovery: ['below', 0.5, 0.12],
      turn: ['below', 0.5, 0.45],
      landing: ['below', 0.5, 0.8],
    },
    ideaSide: { opening: 'left', discovery: 'right', turn: 'left', landing: 'right' },
    objects: [
      { id: 'wave', symbol_id: 'wave', size: 0.1, near: 'discovery', offset: [-320, -40], role: 'mark' },
      { id: 'cloud', symbol_id: 'cloud', size: 0.13, at: [0.82, 0.12], role: 'decisions', side: 'below' },
    ],
    tensionsAt: [0.2, 0.2],
    themeSlots: ['corner-left', 'corner-right', 'left-mid', 'right-mid', 'sky-right', 'flank-right'],
  },
};

TEMPLATES.flow = {
  // A river from its source (upper left) through the rapids to the delta
  // (lower right): the meeting as something that flowed, with turbulence.
  // The band runs corner to corner, so the free paper is the upper-right
  // sky (decisions under the bird, tensions) and the lower-left bank (themes).
  terrain: { horizon: 0.55, below: 'ground', regions: { source: 'water', rapids: 'water', delta: 'water' } },
  palette: { accent: '#2a8fa0', tint: '#d8f0e4', secondary: '#c98a2e' },
  anchor: { symbol_id: 'river', scale: 0.95, placement: 'center-low', opacity: 0.95 },
  route: 'trail',
  stages: {
    opening: ['source', 0.4, 0.5],
    discovery: ['rapids', 0.25, 0.4],
    turn: ['rapids', 0.7, 0.7],
    landing: ['delta', 0.6, 0.5],
  },
  // Ideas hang in the sky over their beat; the river stays clear.
  ideaSide: { opening: 'above', discovery: 'above', turn: 'above', landing: 'above' },
  objects: [
    { id: 'wave', symbol_id: 'wave', size: 0.1, near: 'turn', offset: [60, 60], role: 'mark' },
    { id: 'bird', symbol_id: 'bird', size: 0.12, at: [0.86, 0.3], role: 'decisions', side: 'below' },
    { id: 'sun', symbol_id: 'sun', size: 0.12, at: [0.92, 0.1], role: 'landing-mark' },
  ],
  tensionsAt: [0.5, 0.1],
  themeSlots: ['corner-left', 'base-left', 'left-mid', 'sky-right', 'corner-right', 'right-mid'],
};

TEMPLATES.pressure = {
  // A volcano: pressure builds up the slope, the crater is the turn, the
  // plume is where it all went. Ideas climb the cone; decisions ride the
  // plume with the bird.
  terrain: { horizon: 0.55, below: 'ground' },
  palette: { accent: '#d84a2a', tint: '#f7e2b6', secondary: '#5a6470' },
  anchor: { symbol_id: 'volcano', scale: 0.72, placement: 'center-low', opacity: 0.95 },
  route: 'trail',
  stages: {
    opening: ['base', 0.15, 0.5],
    discovery: ['slope', 0.3, 0.55],
    turn: ['crater', 0.5, 0.6],
    landing: ['plume', 0.55, 0.45],
  },
  ideaSide: { opening: 'left', discovery: 'left', turn: 'right', landing: 'right' },
  objects: [
    { id: 'bird', symbol_id: 'bird', size: 0.12, at: [0.86, 0.22], role: 'decisions', side: 'below' },
    { id: 'sun', symbol_id: 'sun', size: 0.11, at: [0.08, 0.1], role: 'landing-mark' },
  ],
  tensionsAt: [0.22, 0.2],
  themeSlots: ['base-right', 'base-left', 'flank-right', 'corner-right', 'left-mid', 'right-mid', 'sky-right'],
};

TEMPLATES.network = {
  // A web: the meeting starts at the rim, works in through the rings, and
  // lands at the hub. No trail — the strands are the connections; beats
  // are numbered marks on the web.
  terrain: {},
  palette: { accent: '#4b4fb8', tint: '#e2e1f5', secondary: '#d9772e' },
  anchor: { symbol_id: 'web', scale: 0.7, placement: 'center', opacity: 1 },
  route: 'marks',
  stages: {
    opening: ['rim', 0.15, 0.3],
    discovery: ['ring', 0.25, 0.7],
    turn: ['ring', 0.8, 0.35],
    landing: ['hub', 0.5, 0.5],
  },
  ideaSide: { opening: 'left', discovery: 'left', turn: 'right', landing: 'right' },
  objects: [
    { id: 'bird', symbol_id: 'bird', size: 0.11, at: [0.88, 0.2], role: 'decisions', side: 'below' },
    { id: 'star', symbol_id: 'star', size: 0.08, at: [0.1, 0.1], role: 'mark' },
  ],
  tensionsAt: [0.5, 0.08],
  themeSlots: ['corner-left', 'corner-right', 'base-left', 'base-right', 'left-mid', 'right-mid'],
};

TEMPLATES.spiral = {
  // A spiral: each turn goes further — growth over time, cycles, seasons.
  // The opening is the center and the landing the outer turn (TT-1228: the
  // library's spiral expands and matures, as the board's runs outward).
  // Numbered marks; the stroke itself is the route.
  terrain: {},
  palette: { accent: '#2b9c8a', tint: '#d6f0ea', secondary: '#d9772e' },
  anchor: { symbol_id: 'spiral', scale: 0.55, placement: 'center', opacity: 0.9 },
  route: 'marks',
  stages: {
    opening: ['center', 0.5, 0.5],
    discovery: ['middle', 0.85, 0.6],
    turn: ['outer', 0.5, 0.1],
    landing: ['outer', 0.12, 0.7],
  },
  ideaSide: { opening: 'left', discovery: 'right', turn: 'right', landing: 'left' },
  objects: [
    { id: 'sun', symbol_id: 'sun', size: 0.11, at: [0.92, 0.1], role: 'landing-mark' },
    { id: 'star', symbol_id: 'star', size: 0.08, at: [0.08, 0.1], role: 'decisions', side: 'below' },
  ],
  tensionsAt: [0.5, 0.08],
  themeSlots: ['corner-left', 'corner-right', 'base-left', 'base-right', 'left-mid', 'right-mid'],
};

TEMPLATES.layers = {
  // Strata: what lies beneath what. Opening on the surface, landing in the
  // bedrock. A depth route — plumb line + ticks down through the layers.
  // The strata are a band across the lower half, so ideas hang in the sky
  // above them (fanning sideways), the sounding line runs down the left.
  terrain: { horizon: 'surface', below: 'ground', regions: { topsoil: 'underground', clay: 'underground', bedrock: 'underground' } },
  palette: { accent: '#a0642c', tint: '#f2e6c4', secondary: '#5a8f3a' },
  anchor: { symbol_id: 'layers-of-earth', scale: 0.6, placement: 'left', opacity: 0.95 },
  route: 'depth',
  stages: {
    opening: ['surface', 0.2, 0.5],
    discovery: ['topsoil', 0.85, 0.5],
    turn: ['clay', 0.5, 0.5],
    landing: ['bedrock', 0.85, 0.5],
  },
  // Surface/clay ideas hang in the sky; the deeper ones sit on the free
  // paper to the right of the block, level with their stratum.
  ideaSide: { opening: 'above', discovery: 'right', turn: 'above', landing: 'right' },
  objects: [
    { id: 'seed', symbol_id: 'seed-with-shoots', size: 0.12, near: 'opening', offset: [-260, -70], role: 'mark' },
    { id: 'sun', symbol_id: 'sun', size: 0.11, at: [0.92, 0.08], role: 'decisions', side: 'below' },
  ],
  tensionsAt: [0.62, 0.1],
  themeSlots: ['sky-right', 'corner-left', 'corner-right', 'left-mid', 'right-mid', 'base-right'],
};

// ---- TT-1175: classic's most-used primary symbols, as scenes. Each art is
// drawn for a route: a trail to climb (sun, wind, wave, lightning, star),
// a row of stages (moon), or a centre the ideas ring (hand, cloud, flames, eye).

TEMPLATES.making = {
  // A raised hand: the mark people make — making, building, delivering, who
  // does the work (TT-1228: the library's hand). Wrist → palm → thumb → fingertips;
  // the free paper either side of the hand carries the ideas, and the bird
  // the hand lets go of carries the decisions.
  terrain: {},
  palette: { accent: '#3f8f86', tint: '#dcefea', secondary: '#d9772e' },
  anchor: { symbol_id: 'hand', scale: 0.33, placement: 'center-low', opacity: 0.95 },
  route: 'marks',
  stages: {
    opening: ['wrist', 0.5, 0.5],
    discovery: ['palm', 0.4, 0.55],
    turn: ['thumb', 0.4, 0.4],
    landing: ['fingers', 0.5, 0.3],
  },
  ideaSide: { opening: 'right', discovery: 'right', turn: 'left', landing: 'right' },
  objects: [
    { id: 'bird', symbol_id: 'bird', size: 0.11, at: [0.86, 0.14], role: 'decisions', side: 'below' },
  ],
  tensionsAt: [0.22, 0.3],
  themeSlots: ['corner-left', 'corner-right', 'left-mid', 'right-mid', 'base-left', 'base-right', 'sky-right'],
};

TEMPLATES.possibility = {
  // A cloud bank: thinking that has not taken shape yet — dreams, theories,
  // ideas still forming, the fog before clarity. A wisp,
  // into the bank, up to its crown, out on the wisp breaking away — and the
  // sun coming out carries what was decided.
  terrain: {},
  palette: { accent: '#5b7fb5', tint: '#e3ebf7', secondary: '#d9a21e' },
  anchor: { symbol_id: 'cloud', scale: 0.6, placement: 'center-low', opacity: 0.95 },
  route: 'marks',
  stages: {
    opening: ['bank', 0.12, 0.6],
    discovery: ['bank', 0.45, 0.7],
    turn: ['crown', 0.5, 0.35],
    landing: ['wisp-right', 0.5, 0.5],
  },
  ideaSide: { opening: 'left', discovery: 'below', turn: 'above', landing: 'right' },
  objects: [
    { id: 'sun', symbol_id: 'sun', size: 0.12, at: [0.9, 0.12], role: 'decisions', side: 'below' },
  ],
  tensionsAt: [0.5, 0.08],
  themeSlots: ['corner-left', 'corner-right', 'left-mid', 'right-mid', 'base-left', 'base-right'],
};

TEMPLATES.inspiration = {
  // The sun over the horizon: what lights and energizes people — purpose,
  // inspiration, optimism. The
  // beats follow the sun's own day — the left horizon, up over the rays,
  // down the right — as marks: a trail fights the rays' lines.
  terrain: { horizon: 'horizon', below: 'ground' },
  palette: { accent: '#d9902e', tint: '#fbedc9', secondary: '#2a7ae8' },
  anchor: { symbol_id: 'sun', scale: 0.78, placement: 'center-low', opacity: 0.9 },
  route: 'marks',
  stages: {
    opening: ['horizon', 0.1, 0.1],
    discovery: ['rays', 0.2, 0.45],
    turn: ['rays', 0.5, 0.12],
    landing: ['rays', 0.85, 0.45],
  },
  ideaSide: { opening: 'left', discovery: 'left', turn: 'right', landing: 'right' },
  objects: [
    { id: 'bird', symbol_id: 'bird', size: 0.1, at: [0.9, 0.12], role: 'decisions', side: 'below' },
  ],
  tensionsAt: [0.5, 0.06],
  themeSlots: ['corner-left', 'corner-right', 'base-left', 'base-right', 'left-mid', 'right-mid'],
};

TEMPLATES.turbulence = {
  // Gusts of wind sweeping left to right: outside forces stirring things up —
  // chaos, influence, headwinds and tailwinds. The trail rides the gusts; ideas hang off the band above and
  // below it.
  terrain: {},
  palette: { accent: '#4a9a8c', tint: '#e0f0ec', secondary: '#d9772e' },
  anchor: { symbol_id: 'wind', scale: 0.85, placement: 'center', opacity: 0.95 },
  route: 'trail',
  stages: {
    opening: ['upwind', 0.2, 0.5],
    discovery: ['gust', 0.2, 0.6],
    turn: ['gust', 0.85, 0.45],
    landing: ['curls', 0.5, 0.3],
  },
  ideaSide: { opening: 'above', discovery: 'below', turn: 'below', landing: 'above' },
  objects: [
    { id: 'bird', symbol_id: 'bird', size: 0.1, at: [0.92, 0.1], role: 'decisions', side: 'below' },
  ],
  tensionsAt: [0.5, 0.08],
  themeSlots: ['corner-left', 'corner-right', 'base-left', 'base-right', 'left-mid', 'right-mid'],
};

TEMPLATES.change = {
  // One great wave: the swell builds, the face rises, the crest curls, it
  // breaks. A transformation that moves in phases (the library's wave:
  // change that is methodical). The sky above the
  // wave is the free paper.
  terrain: { horizon: 0.45, below: 'water' },
  palette: { accent: '#1f7f99', tint: '#d3eff4', secondary: '#d9772e' },
  anchor: { symbol_id: 'wave', scale: 0.85, placement: 'center-low', opacity: 0.95 },
  route: 'trail',
  stages: {
    opening: ['swell', 0.3, 0.3],
    discovery: ['face', 0.25, 0.45],
    turn: ['crest', 0.35, 0.35],
    landing: ['break', 0.6, 0.45],
  },
  ideaSide: { opening: 'above', discovery: 'above', turn: 'above', landing: 'right' },
  objects: [
    { id: 'bird', symbol_id: 'bird', size: 0.1, at: [0.9, 0.12], role: 'decisions', side: 'below' },
  ],
  tensionsAt: [0.2, 0.2],
  themeSlots: ['corner-left', 'corner-right', 'left-mid', 'sky-right', 'base-left', 'base-right'],
};

TEMPLATES.renewal = {
  // A fire: fuel, the body of the fire, the tongues, the sparks that fly.
  // Burning away what is no longer needed so something new can start — a
  // burning platform, letting go — or a literal fire. The ideas ring
  // the flames.
  terrain: { horizon: 'logs', below: 'ground' },
  palette: { accent: '#d8582a', tint: '#fbe3c4', secondary: '#5a6470' },
  anchor: { symbol_id: 'flames', scale: 0.5, placement: 'center-low', opacity: 0.95 },
  route: 'marks',
  stages: {
    opening: ['logs', 0.5, 0.5],
    discovery: ['body', 0.3, 0.6],
    turn: ['body', 0.7, 0.4],
    landing: ['sparks', 0.5, 0.3],
  },
  ideaSide: { opening: 'left', discovery: 'left', turn: 'right', landing: 'right' },
  objects: [],
  tensionsAt: [0.2, 0.22],
  themeSlots: ['corner-left', 'corner-right', 'left-mid', 'right-mid', 'base-left', 'base-right', 'sky-right'],
};

TEMPLATES.shock = {
  // A storm cloud and the bolt from it: what built up, then the strike —
  // a sudden disruption, a threat, a wake-up call. The trail runs down
  // the bolt to where it hits.
  terrain: { horizon: 'ground', below: 'ground' },
  palette: { accent: '#6a5bb5', tint: '#e8e4f6', secondary: '#d9a21e' },
  anchor: { symbol_id: 'sky-lightning', scale: 0.62, placement: 'right', opacity: 0.95 },
  route: 'trail',
  stages: {
    opening: ['cloud', 0.25, 0.6],
    discovery: ['cloud', 0.75, 0.55],
    turn: ['bolt', 0.6, 0.35],
    landing: ['strike', 0.45, 0.6],
  },
  ideaSide: { opening: 'left', discovery: 'above', turn: 'right', landing: 'left' },
  objects: [],
  tensionsAt: [0.2, 0.3],
  themeSlots: ['corner-left', 'left-mid', 'corner-right', 'base-left', 'base-right', 'right-mid'],
};

TEMPLATES.shadow = {
  // The moon, left to right from shadow to full light: the shadow side —
  // introspection, grief, what is kept quiet — and what comes to light.
  // Each beat is a phase; ideas alternate above and below the row.
  terrain: {},
  palette: { accent: '#6b7a99', tint: '#eef0f6', secondary: '#c9a22e' },
  anchor: { symbol_id: 'moon', scale: 0.7, placement: 'center', opacity: 0.95 },
  route: 'marks',
  stages: {
    opening: ['crescent', 0.5, 0.5],
    discovery: ['half', 0.5, 0.5],
    turn: ['gibbous', 0.5, 0.5],
    landing: ['full', 0.5, 0.5],
  },
  ideaSide: { opening: 'above', discovery: 'below', turn: 'above', landing: 'below' },
  objects: [
    { id: 'star', symbol_id: 'star', size: 0.08, at: [0.92, 0.1], role: 'decisions', side: 'below' },
  ],
  tensionsAt: [0.5, 0.08],
  themeSlots: ['corner-left', 'corner-right', 'base-left', 'base-right', 'left-mid', 'right-mid'],
};

TEMPLATES.aspiration = {
  // A guiding star and its trail: aspiration, a north star, ambition, and
  // recognition — wins celebrated, people appreciated. The
  // trail rises from the lower left to the star; what was decided stands
  // with the flag on the free paper under the trail.
  terrain: {},
  palette: { accent: '#c9921e', tint: '#fbf0c9', secondary: '#2a7ae8' },
  anchor: { symbol_id: 'star', scale: 0.7, placement: 'center-low', opacity: 0.95 },
  route: 'trail',
  stages: {
    opening: ['tail', 0.3, 0.55],
    discovery: ['trail', 0.2, 0.7],
    turn: ['trail', 0.75, 0.3],
    landing: ['star', 0.4, 0.6],
  },
  ideaSide: { opening: 'above', discovery: 'above', turn: 'above', landing: 'left' },
  objects: [
    { id: 'flag', symbol_id: 'person-holding-a-flag', size: 0.17, at: [0.9, 0.78], role: 'decisions', side: 'left' },
  ],
  tensionsAt: [0.3, 0.2],
  themeSlots: ['corner-left', 'base-right', 'left-mid', 'right-mid', 'corner-right', 'base-left'],
};

TEMPLATES.vision = {
  // An open eye: vision, perspective, attention, what we see. The beats ring
  // the eye — one corner, the lid, the other corner — and land on the iris.
  terrain: {},
  palette: { accent: '#b8641e', tint: '#f3e8dc', secondary: '#6a5bb5' },
  anchor: { symbol_id: 'eye', scale: 0.6, placement: 'center', opacity: 0.95 },
  route: 'marks',
  stages: {
    opening: ['eye', 0.04, 0.55],
    discovery: ['lid', 0.5, 0.1],
    turn: ['eye', 0.96, 0.5],
    landing: ['iris', 0.5, 0.5],
  },
  ideaSide: { opening: 'left', discovery: 'above', turn: 'right', landing: 'below' },
  objects: [],
  tensionsAt: [0.5, 0.06],
  themeSlots: ['corner-left', 'corner-right', 'base-left', 'base-right', 'left-mid', 'right-mid'],
};

// ---- TT-1175 round 2: the rest of classic's published primary symbols,
// framed by the artists' own symbol definitions.

TEMPLATES.care = {
  // A triskelion — "deep care from one person to another": giving, healing.
  // Each arm is a stage; the meeting lands where the three meet.
  terrain: {},
  palette: { accent: '#d9694a', tint: '#fbe4d8', secondary: '#2a7ae8' },
  anchor: { symbol_id: 'triskelion', scale: 0.36, placement: 'center', opacity: 0.95 },
  route: 'marks',
  stages: {
    opening: ['arm-left', 0.3, 0.6],
    discovery: ['arm-top', 0.5, 0.3],
    turn: ['arm-right', 0.7, 0.6],
    landing: ['center', 0.5, 0.5],
  },
  ideaSide: { opening: 'left', discovery: 'right', turn: 'right', landing: 'below' },
  objects: [],
  tensionsAt: [0.84, 0.14],
  themeSlots: ['corner-left', 'corner-right', 'left-mid', 'right-mid', 'base-left', 'base-right', 'sky-right'],
};

TEMPLATES.community = {
  // A mandala — balance made together: community, collaboration. From the
  // rim, around the rings, in to the flower at the centre.
  terrain: {},
  palette: { accent: '#3f8f86', tint: '#e3f1ec', secondary: '#d9772e' },
  anchor: { symbol_id: 'mandala', scale: 0.4, placement: 'center', opacity: 0.9 },
  route: 'marks',
  stages: {
    opening: ['rim', 0.5, 0.03],
    discovery: ['ring', 0.05, 0.5],
    turn: ['ring', 0.95, 0.5],
    landing: ['center', 0.5, 0.5],
  },
  ideaSide: { opening: 'left', discovery: 'left', turn: 'right', landing: 'below' },
  // The ring marks label above, not inward: inward, three labels met in the
  // small centre around the landing mark (TT-1229).
  labelSide: { discovery: 'above', turn: 'above' },
  objects: [],
  tensionsAt: [0.84, 0.14],
  themeSlots: ['corner-left', 'corner-right', 'left-mid', 'right-mid', 'base-left', 'base-right', 'sky-right'],
};

TEMPLATES.elephant = {
  // The elephant in the room: what dominates and goes unsaid. Noticed by
  // its legs, then its bulk, then its head — and the trunk is where the
  // room finally names it.
  terrain: { horizon: 'ground', below: 'ground' },
  palette: { accent: '#7a6f99', tint: '#ebe8f3', secondary: '#d9772e' },
  anchor: { symbol_id: 'elephant', scale: 0.62, placement: 'right', opacity: 0.95 },
  route: 'marks',
  stages: {
    opening: ['legs', 0.85, 0.5],
    discovery: ['body', 0.6, 0.4],
    turn: ['head', 0.5, 0.35],
    landing: ['trunk', 0.3, 0.75],
  },
  ideaSide: { opening: 'right', discovery: 'above', turn: 'left', landing: 'left' },
  objects: [],
  tensionsAt: [0.2, 0.3],
  themeSlots: ['corner-left', 'left-mid', 'base-right', 'corner-right', 'right-mid', 'base-left'],
};

TEMPLATES.crossroads = {
  // An X — "a crossroads or a puzzle", and X marks the spot: up the road, to
  // the crossing, the signpost, and out on the road taken.
  terrain: { horizon: 0.35, below: 'ground' },
  palette: { accent: '#b8862e', tint: '#f7ecd2', secondary: '#3c6fa8' },
  anchor: { symbol_id: 'x', scale: 0.82, placement: 'center-low', opacity: 0.95 },
  route: 'trail',
  stages: {
    opening: ['approach', 0.3, 0.45],
    discovery: ['approach', 0.85, 0.1],
    turn: ['crossing', 0.5, 0.5],
    landing: ['fork', 0.6, 0.55],
  },
  ideaSide: { opening: 'left', discovery: 'left', turn: 'above', landing: 'above' },
  objects: [],
  tensionsAt: [0.2, 0.14],
  themeSlots: ['corner-left', 'corner-right', 'base-left', 'base-right', 'left-mid', 'right-mid'],
};

TEMPLATES.mind = {
  // A head in profile — mental activity, knowledge inside a mind. From the
  // neck up into the brain, out through the face: ideas leave the head into
  // the open paper in front of it.
  terrain: {},
  palette: { accent: '#8a5bb5', tint: '#efe6f7', secondary: '#2a9c8a' },
  anchor: { symbol_id: 'profile-with-brain', scale: 0.32, placement: 'left', opacity: 0.95 },
  route: 'marks',
  stages: {
    opening: ['neck', 0.5, 0.5],
    discovery: ['brain', 0.25, 0.5],
    turn: ['brain', 0.8, 0.4],
    landing: ['face', 0.85, 0.3],
  },
  ideaSide: { opening: 'right', discovery: 'right', turn: 'right', landing: 'right' },
  objects: [],
  tensionsAt: [0.7, 0.12],
  themeSlots: ['corner-right', 'right-mid', 'base-right', 'sky-right', 'corner-left', 'base-left'],
};

TEMPLATES.dilemma = {
  // A snake winding from tail to head — a dilemma weighed back and forth
  // until a decision. The trail rides its body; the head is the decision.
  terrain: {},
  palette: { accent: '#7a9a2e', tint: '#eef4d6', secondary: '#d9772e' },
  anchor: { symbol_id: 'snake', scale: 0.85, placement: 'center-low', opacity: 0.95 },
  route: 'trail',
  stages: {
    opening: ['tail', 0.5, 0.5],
    discovery: ['coil1', 0.6, 0.8],
    turn: ['coil2', 0.5, 0.2],
    landing: ['head', 0.4, 0.6],
  },
  ideaSide: { opening: 'above', discovery: 'below', turn: 'above', landing: 'left' },
  objects: [],
  tensionsAt: [0.2, 0.2],
  themeSlots: ['corner-left', 'corner-right', 'left-mid', 'base-left', 'base-right', 'right-mid'],
};

TEMPLATES.perseverance = {
  // A turtle walking uphill — forward slowly, though it is hard. The trail
  // goes over its shell to its head and on up the path; the flag ahead
  // holds what was decided.
  terrain: { horizon: 0.72, below: 'ground' },
  palette: { accent: '#6f8a3a', tint: '#eef2dc', secondary: '#d9772e' },
  anchor: { symbol_id: 'turtle', scale: 0.7, placement: 'center-low', opacity: 0.95 },
  route: 'trail',
  stages: {
    opening: ['shell', 0.12, 0.75],
    discovery: ['shell', 0.45, 0.25],
    turn: ['head', 0.6, 0.4],
    landing: ['ahead', 0.65, 0.3],
  },
  ideaSide: { opening: 'left', discovery: 'above', turn: 'above', landing: 'right' },
  objects: [
    { id: 'flag', symbol_id: 'person-holding-a-flag', size: 0.17, near: 'landing', offset: [120, -130], role: 'decisions', side: 'left' },
  ],
  tensionsAt: [0.2, 0.18],
  themeSlots: ['corner-left', 'corner-right', 'left-mid', 'sky-right', 'base-left', 'base-right'],
};

TEMPLATES.shelter = {
  // A geodesic dome — shelter, protection, well-being. Up from the ground,
  // over the dome, and in at the door.
  terrain: { horizon: 'ground', below: 'ground' },
  palette: { accent: '#3a8fa0', tint: '#e0f1f3', secondary: '#c9a22e' },
  anchor: { symbol_id: 'buckyball', scale: 0.62, placement: 'center-low', opacity: 0.95 },
  route: 'marks',
  stages: {
    opening: ['ground', 0.12, 0.3],
    discovery: ['dome', 0.12, 0.55],
    turn: ['dome', 0.5, 0.08],
    landing: ['door', 0.5, 0.45],
  },
  ideaSide: { opening: 'left', discovery: 'left', turn: 'above', landing: 'right' },
  objects: [
    { id: 'sun', symbol_id: 'sun', size: 0.11, at: [0.9, 0.12], role: 'decisions', side: 'below' },
  ],
  tensionsAt: [0.2, 0.14],
  themeSlots: ['corner-left', 'corner-right', 'left-mid', 'right-mid', 'base-left', 'base-right'],
};

TEMPLATES.future = {
  // A bird lifting off — beyond the present, toward the future. The trail
  // follows its flight up from the lower left to the raised wing; the sun
  // it flies toward carries the decisions.
  terrain: {},
  palette: { accent: '#2f6f9a', tint: '#dcecf5', secondary: '#d9772e' },
  anchor: { symbol_id: 'bird', scale: 0.6, placement: 'center-low', opacity: 0.95 },
  route: 'trail',
  stages: {
    opening: ['trail', 0.15, 0.6],
    discovery: ['trail', 0.6, 0.25],
    turn: ['body', 0.4, 0.5],
    landing: ['wing', 0.6, 0.15],
  },
  ideaSide: { opening: 'above', discovery: 'below', turn: 'left', landing: 'right' },
  objects: [
    { id: 'sun', symbol_id: 'sun', size: 0.11, at: [0.92, 0.12], role: 'decisions', side: 'below' },
  ],
  tensionsAt: [0.2, 0.18],
  themeSlots: ['corner-left', 'corner-right', 'left-mid', 'right-mid', 'base-left', 'base-right'],
};

TEMPLATES.open = {
  // TT-1169: no backdrop — the graphic-recorder's open board (ImageThink,
  // Ink Factory). A big title centre-top, the arc as a hand-drawn swoosh
  // across the sheet with numbered marks, ideas at spot-drawing size along
  // it, themes in the corners. The planner's default when no backdrop's
  // picture is literally the meeting's subject.
  terrain: {},
  palette: { accent: '#2a7ae8', tint: '#dde9f8', secondary: '#d9772e' },
  anchor: null,
  route: 'arc',
  title: { center: true, word: 120, maxFrac: 0.55 },
  ideaIcon: 140,
  stages: {
    opening: ['sheet', 0.1, 0.6],
    discovery: ['sheet', 0.36, 0.44],
    turn: ['sheet', 0.62, 0.6],
    landing: ['sheet', 0.88, 0.42],
  },
  ideaSide: { opening: 'below', discovery: 'above', turn: 'below', landing: 'above' },
  objects: [],
  tensionsAt: [0.84, 0.12],
  themeSlots: ['corner-left', 'corner-right', 'left-mid', 'right-mid', 'base-left', 'base-right', 'sky-right'],
};

// Keys the planner used to emit (TT-1228 renames, platform
// scenegen/metaphors.py ALIASES): a stored spec that names one still draws.
// `change` was the wind's key; it is the wave's now.
const ALIASES = {
  support: 'making',
  uncertainty: 'possibility',
  dawn: 'inspiration',
  momentum: 'change',
  fire: 'renewal',
  breakthrough: 'shock',
  cycles: 'shadow',
  journey: 'climb',
};

// A metaphor this renderer has no template for draws the open board (no
// art), as the planner does with an answer outside its enum — never a
// mountain by accident.
export function templateFor(metaphor) {
  return TEMPLATES[ALIASES[metaphor] || metaphor] || TEMPLATES.open;
}

/** The art a story spec draws, its template's, as { symbolId, role }: the anchor a backdrop, each object a mid. */
export function templateArt(metaphor) {
  const t = templateFor(metaphor);
  return [
    ...(t.anchor ? [{ symbolId: t.anchor.symbol_id, role: 'backdrop' }] : []),
    ...t.objects.map((o) => ({ symbolId: o.symbol_id, role: 'mid' })),
  ];
}
